/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, { useLayoutEffect, useRef, useState } from 'react';
import { getIsoToUsShortDateTime } from '@/utils/isoToUsShortDateTime';
import { NormalizedMessage } from 'types/prompt-sequence-view';

import {
  alwaysRenderStringFormatToUI,
  handlePromptDataVarient,
} from '@/lib/helper';
import { useSplitPromptSequence } from '@/hooks/useSplitPromptSequence';

import { PromptSequenceIcons } from '../icons/icons';

interface CustomPromptComponentProps {
  data: NormalizedMessage | string;
  isFirstInput?: boolean;
  index: number;
  onSplit?: () => void;
  status: string;
  time: string;
  totalTokens?: number;
  completionTokens?: number;
  promptTokens?: number;
  conversationType?: 'Input' | 'Output';
  span_id: string;
}

export const CustomPromptComponentConversations: React.FC<
  CustomPromptComponentProps
> = ({
  data,
  isFirstInput = false,
  index,
  onSplit,
  status,
  time,
  totalTokens,
  completionTokens,
  promptTokens,
  conversationType = 'Input',
  span_id,
}) => {
  const [isClamped, setIsClamped] = useState<boolean>(false);

  const formattedDateTime = getIsoToUsShortDateTime(time);

  const paraRef = useRef<HTMLParagraphElement>(null);

  const { setSplitPayload, splitPayload } = useSplitPromptSequence();

  const isHighlighted =
    splitPayload !== null &&
    splitPayload.splitFromIndex === index &&
    splitPayload.span_id === span_id;

  // Check if the data is a type of string.
  const isString = typeof data === 'string';

  // Check if the data is a type of object.
  const entries = !isString ? Object.entries(data) : [];

  // Check if the data is an empty
  const isEmpty = !isString && entries.length === 0;

  // If the data is string type, add 'output' as role. Otherwise get the role from the existing data.
  const role = isString ? 'output' : data.role;

  // If the data is string type ... render it on to the UI. Otherwise get the "content" from the existing data object.
  const content = isString ? data : data.content;

  // Handle the display response.
  const displayResponse = handlePromptDataVarient(content);

  useLayoutEffect(() => {
    function checkClamp() {
      // Get the p element.
      const el = paraRef.current;

      // Early return if we don't have p element
      if (!el) return;

      // Check if the prompt is more than in two lines, if so then show a button
      setIsClamped(el.scrollHeight > el.clientHeight);
    }
    // Initial check
    checkClamp();

    // Check if the screen resizes.
    window.addEventListener('resize', checkClamp);

    // Remove event listener.
    return () => {
      window.removeEventListener('resize', checkClamp);
    };
  }, [displayResponse]);

  //  Early return for the empty data.
  if (isEmpty) {
    return null;
  }

  // Create a separate ROLE component that can be reused in 2 places within this component.
  const PromptRole = () => {
    return (
      <p
        className={`text-body-sm leading-4 tracking-[0%] ${
          role === 'user' ? 'text-[#768FCC]' : 'text-[#8C9EA1]'
        }`}
      >
        {role === 'output' ? '' : role.length > 1 ? role : ''}
      </p>
    );
  };

  // On click on to the split button pass the required data to the context.
  function handleSplitClick() {
    setSplitPayload({
      longPrompt: displayResponse,
      splitFromIndex: index!,
      span_id,
      role: <PromptRole />,
    });
    onSplit?.();
  }

  return (
    <div className="relative w-fit">
      <div>
        <div className="flex flex-col gap-1">
          {isFirstInput && (
            <p className="text-[11px] tracking-[0%] text-form-input-disabled">
              {conversationType}
            </p>
          )}
          <div
            className={`relative rounded-bl-lg rounded-br-lg rounded-tr-lg px-4 py-[10px] ${
              status === 'error' ? 'bg-[#F26C743D]' : 'bg-[#0A2D3A]'
            }`}
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <div>
                <PromptRole />
                <div className="group flex items-start">
                  <p
                    ref={paraRef}
                    className="mt-0.5 line-clamp-2 break-words break-all text-[14px] font-normal leading-[140%] tracking-[0%] text-[#EBEFF1]"
                  >
                    {alwaysRenderStringFormatToUI(displayResponse)}
                  </p>
                  {isClamped && (
                    <div
                      className={`absolute right-0 top-0 flex h-full w-40 items-center justify-end rounded-br-lg rounded-tr-lg  bg-gradient-to-r from-[#0A2D3A00] to-[#0A2D3A] opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${role === 'output' ? 'from-[#16595600] to-[#165956]' : ''}`}
                    >
                      <button
                        onClick={handleSplitClick}
                        className="pr-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                      >
                        {PromptSequenceIcons.SplitIcon(
                          `${role === 'output' ? 'text-jade' : 'text-ocean'}`
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Eval badge, time and token goes here */}
              <div
                className={`text-body-sm tracking-[2%] text-body-subtle ${
                  role === 'human' ? 'text-white/55' : 'text-body-subtle'
                } mt-2 flex  w-full items-center justify-between`}
              >
                <div className="ml-auto">
                  <span>
                    {role === 'output' && totalTokens !== undefined && (
                      <>
                        <span>{`Token: ${totalTokens}`}</span>
                        {(completionTokens !== undefined ||
                          promptTokens !== undefined) && (
                          <span>
                            {` - `}
                            {completionTokens !== undefined &&
                              `${completionTokens} Completion`}
                            {completionTokens !== undefined &&
                              promptTokens !== undefined &&
                              `,  `}
                            {promptTokens !== undefined &&
                              `${promptTokens} Prompts`}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                  <span>{formattedDateTime}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. The overlay, rendered on top when highlighted */}
      {isHighlighted && (
        <div className="absolute -inset-y-1.5 inset-x-0 border-l border-[#8C9EA1] bg-[#FFFFFF14]"></div>
      )}
    </div>
  );
};
